import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { handleTenantSuspended, isTenantSuspendedError, TENANT_SUSPENDED_NOTICE } from 'src/utils/tenantSuspension';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// ============================================================================
// Types
// ============================================================================

export interface UserRole {
  id: number;
  name: string;
}

export interface EmployeeId {
  id: number;
  employeeId?: number;
  userType?: number;
  profilePicture?: string;
}

export interface User {
  access_token: string;
  expires_in?: number;
  userName?: string;
  email: string;
  partyId: number;
  firstName: string;
  lastName: string;
  contactNo?: string;
  userId: number;
  employeeId?: EmployeeId;
  partyType?: number;
  roles: UserRole[];
  role?: UserRole; // Some responses use singular 'role'
  immediateSupervisorPartyId?: number | null;
  jobDeatils?: {
    peopleManager?: string;
    [key: string]: any;
  };
  operations?: any[];
  clientId?: number;
  clientName?: string;
  tenantId?: number; // Tenant ID from JWT
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  sessionChecked: boolean;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface GoogleLoginPayload {
  token: string;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  sessionChecked: false
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Save user session to sessionStorage (maintains backward compatibility)
 */
const saveSessionToStorage = (user: User) => {
  sessionStorage.setItem('LoginInfo', JSON.stringify({ login: true }));
  sessionStorage.setItem('UserDetail', JSON.stringify(user));
  sessionStorage.setItem('UserEmail', JSON.stringify(user.email));
  sessionStorage.setItem('UserpartyId', JSON.stringify(user.partyId));
  sessionStorage.setItem('UserId', user.userId?.toString() || '');
  sessionStorage.setItem('employeeId', user.employeeId?.id?.toString() || '');
  sessionStorage.setItem('operations', JSON.stringify(user.operations || []));
  sessionStorage.setItem('immediateSupervisorPartyId', JSON.stringify(user.immediateSupervisorPartyId));
  sessionStorage.setItem('peopleManager', JSON.stringify(user.jobDeatils?.peopleManager));
};

/**
 * Save auth token to cookie
 */
const saveTokenToCookie = (token: string) => {
  const expires = new Date();
  expires.setDate(expires.getDate() + 1);
  document.cookie = `auth_token=${token}; expires=${expires.toUTCString()}; path=/; SameSite=Strict`;
};

/**
 * Clear all session data
 * Note: Does NOT clear localStorage tenant persistence (lastUsedTenant)
 * so users return to their last tenant when they log back in.
 * Call clearStoredTenant() from tenantUtils if you need to clear that too.
 */
const clearAllSession = () => {
  sessionStorage.clear();
  document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  // Intentionally NOT clearing localStorage.getItem('lastUsedTenant') for user convenience
};

/**
 * Get auth token from cookie
 */
const getTokenFromCookie = (): string | null => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'auth_token') {
      return value;
    }
  }
  return null;
};

/**
 * Try to load user from sessionStorage on initial load
 */
const loadUserFromStorage = (): User | null => {
  try {
    const userDetailString = sessionStorage.getItem('UserDetail');
    if (userDetailString) {
      return JSON.parse(userDetailString) as User;
    }
  } catch (e) {
    console.error('Failed to parse UserDetail from sessionStorage:', e);
  }
  return null;
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Login with email/username and password
 */
export const loginUser = createAsyncThunk<User, LoginCredentials, { rejectValue: string }>(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, credentials);

      if (response.status !== 200 && response.status !== 201) {
        return rejectWithValue('Invalid credentials');
      }

      const user: User = response.data;

      if (!user.access_token) {
        return rejectWithValue('No access token received');
      }

      // Save to storage for backward compatibility
      saveSessionToStorage(user);
      saveTokenToCookie(user.access_token);

      return user;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed';
      return rejectWithValue(message);
    }
  }
);

/**
 * Login with Google OAuth
 */
export const loginWithGoogle = createAsyncThunk<User, GoogleLoginPayload, { rejectValue: string }>(
  'auth/loginWithGoogle',
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/google-login`, { token });

      if (response.status !== 200 && response.status !== 201) {
        return rejectWithValue('Google login failed');
      }

      const user: User = response.data;

      if (!user.access_token) {
        return rejectWithValue('No access token received');
      }

      // Save to storage for backward compatibility
      saveSessionToStorage(user);
      saveTokenToCookie(user.access_token);

      return user;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Google login failed';
      return rejectWithValue(message);
    }
  }
);

/**
 * Shape of GET /auth/session (backend-go internal/modules/auth/usecases.
 * ToSessionPayload, built from the SAME LoginResponse assembly /auth/login
 * uses). Identical UserDetail shape to a normal login response, EXCEPT:
 *   - `access_token` is never present in the body (cookie-only auth — the
 *     hris_session HttpOnly cookie is the sole credential).
 *   - `tenantId` is always present, including the platform-admin sentinel 0.
 */
type SessionResponse = Omit<User, 'access_token'> & { tenantId: number };

/**
 * Restore session from cookie token
 */
export const restoreSession = createAsyncThunk<User, void, { rejectValue: string }>(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    try {
      // First try to load from sessionStorage
      const storedUser = loadUserFromStorage();
      const tokenFromCookie = getTokenFromCookie();

      // If we have user in storage and token in cookie, validate with backend
      const tokenToValidate = storedUser?.access_token || tokenFromCookie;

      if (tokenToValidate) {
        const response = await axios.post(
          `${API_BASE_URL}/auth/restore-session`,
          { token: tokenToValidate },
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (response.data.isValid && response.data.user) {
          const user: User = response.data.user;

          // Update storage with fresh data
          saveSessionToStorage(user);
          if (tokenToValidate) {
            saveTokenToCookie(tokenToValidate);
          }

          return user;
        }

        // The stored bearer token is no longer valid (expired, or revoked by a platform logout).
        // Drop it, then try the SSO cookie session below: after an SSO re-login the browser holds
        // a fresh hris_session cookie, and keeping the old token would 401 every API call.
        clearAllSession();
      }

      // No localStorage bearer token AND no legacy auth_token cookie — this is
      // the crelo SSO cookie-only session path (flag-gated server-side by
      // CRELO_SSO_ENABLED; see backend-go/cmd/main.go + auth_handler.go
      // GetSession()). The browser auto-sends the HttpOnly hris_session
      // cookie same-origin, so we always attempt GET /auth/session here. When
      // CRELO_SSO_ENABLED=false (or there simply is no SSO session), the
      // route does not exist server-side (404) — bearer-only users see ZERO
      // behavior change, they just take one extra (cheap, same-origin) round
      // trip before the existing "No token found" rejection.
      let sessionData: SessionResponse | undefined;
      try {
        const sessionResponse = await axios.get(`${API_BASE_URL}/auth/session`, { withCredentials: true });
        sessionData = sessionResponse.data;
      } catch {
        // No hris_session cookie (flag off, 404, or genuinely never logged
        // in) — same outcome as the pre-SSO "no token at all" case. Do NOT
        // clearAllSession() here (nothing was ever stored) and keep the
        // original rejectValue so this remains behaviorally identical to
        // before this cookie-fallback was added.
        return rejectWithValue('No token found');
      }
      if (!sessionData || !sessionData.email) {
        return rejectWithValue('No token found');
      }

      // COOKIE-ONLY: access_token is intentionally set to an empty string —
      // the actual JWT lives ONLY in the HttpOnly hris_session cookie and is
      // never exposed to JS; all subsequent API calls ride on that cookie
      // automatically (see providers/axios-instance.ts withCredentials).
      // Callers that gate purely on `user.access_token` truthiness (rather
      // than Redux `isAuthenticated`) will treat a cookie session as "no
      // token" — see the enumerated legacy stragglers in the session report.
      const user: User = { ...sessionData, access_token: '' };

      // Populate sessionStorage via the same helper login uses, so the many
      // legacy call sites that read sessionStorage directly (party id,
      // email, employee id, roles, operations, immediateSupervisorPartyId,
      // etc.) see a fully-populated session — everything /auth/login would
      // have given them EXCEPT the bearer token, which intentionally stays
      // cookie-only.
      saveSessionToStorage(user);
      return user;
    } catch (error: any) {
      clearAllSession();
      // Organisation suspended: restore-session answers 403 (not
      // {isValid:false}). Same clean logout, plus a one-time notice; the
      // rejected state sends SessionManager to /login, so no redirect here.
      if (isTenantSuspendedError(error)) {
        handleTenantSuspended(false);
        return rejectWithValue(TENANT_SUSPENDED_NOTICE);
      }
      return rejectWithValue('Session restoration failed');
    }
  }
);

/**
 * True when `user` represents a crelo-platform SSO (cookie-only) session
 * rather than a normal bearer-token login.
 *
 * Signal: SSO sessions are hydrated by restoreSession()'s GET /auth/session
 * fallback (see above), which deliberately sets `access_token: ''` — the
 * real credential lives ONLY in the HttpOnly hris_session cookie and is
 * never exposed to JS. A normal bearer login (password or Google) ALWAYS
 * has a non-empty access_token. So "user present but no access_token" is a
 * sufficient, self-contained signal — it does not require knowing whether
 * CRELO_SSO_ENABLED is set at runtime.
 */
export const isSSOCookieSession = (user: User | null | undefined): boolean =>
  !!user && !user.access_token;

/**
 * Logout user
 */
export const logoutUser = createAsyncThunk<void, void>('auth/logoutUser', async () => {
  clearAllSession();
  // Optionally call backend logout endpoint
  // await axios.post(`${API_BASE_URL}/auth/logout`);
});

// ============================================================================
// Slice
// ============================================================================

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Synchronously set user (useful for hydration from sessionStorage)
     */
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.token = action.payload?.access_token || null;
      state.isAuthenticated = !!action.payload;
    },

    /**
     * Clear any auth errors
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Mark session as checked (prevents infinite loops on public routes)
     */
    markSessionChecked: (state) => {
      state.sessionChecked = true;
    },

    /**
     * Hydrate state from sessionStorage on app init
     * Also marks session as checked to prevent unnecessary restoreSession calls
     */
    hydrateFromStorage: (state) => {
      const user = loadUserFromStorage();
      if (user) {
        state.user = user;
        state.token = user.access_token;
        state.isAuthenticated = true;
        state.sessionChecked = true; // Prevent unnecessary restoreSession call
      }
    }
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.token = action.payload.access_token;
        state.isAuthenticated = true;
        state.error = null;
        state.sessionChecked = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Login failed';
        state.isAuthenticated = false;
      });

    // Google Login
    builder
      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.token = action.payload.access_token;
        state.isAuthenticated = true;
        state.error = null;
        state.sessionChecked = true;
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Google login failed';
        state.isAuthenticated = false;
      });

    // Restore Session
    builder
      .addCase(restoreSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.token = action.payload.access_token;
        state.isAuthenticated = true;
        state.sessionChecked = true;
      })
      .addCase(restoreSession.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.sessionChecked = true;
      });

    // Logout
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.sessionChecked = true;
    });
  }
});

// ============================================================================
// Exports
// ============================================================================

export const { setUser, clearError, markSessionChecked, hydrateFromStorage } = authSlice.actions;

export default authSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

// Note: RootState type will be inferred from store.tsx
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.loading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectSessionChecked = (state: { auth: AuthState }) => state.auth.sessionChecked;
export const selectUserRoleId = (state: { auth: AuthState }) => state.auth.user?.roles?.[0]?.id || state.auth.user?.role?.id || null;
